import { createQuery } from '@tanstack/solid-query';
import {
  createContext,
  createEffect,
  JSXElement,
  mergeProps,
  Show,
  untrack,
  useContext
} from 'solid-js';
import {
  getCurrentPortalCustomizationConfig,
  getHostedAuthCustomization,
  getHostedAuthPageCustomizationQueryKey,
  getPortalCustomizationConfigQuery,
  retryServerErrors
} from '~/api';
import './portal-customisation-base.scss';
import SkLoader from '../components/ui/SkLoader';
import {
  ApplicationCustomizationSettings,
  CustomizationSettings,
  PortalCustomizationSettings
} from '~/api/types/customisationData.ts';
import { createStore } from 'solid-js/store';
import { HttpStatusCode } from 'axios';
import logger from '~/utils/logger';
import { applyBrandCustomization } from '~/portal-customization/utils.ts';
import { AppErrors } from '~/components/ErrorBoundaries/utils.ts';
import { globalStore, setGlobalStore } from '~/stores';
import { AppContexts } from '~/utils/commonUtils.ts';
import {
  PreviewModeContextValueType,
  usePreviewModeContext
} from '~/components/AppData/PreviewModeContext.tsx';
import {
  askCustomizationFromParent,
  PREVIEW_IFRAME_MESSAGES,
  registerPreviewEvent
} from '~/pages/Customizations/previewIframeUtils.ts';
import {
  getCustomizationSettingsWithDefaults,
  getDefaultApplicationCustomizationSettings,
  getDefaultPortalCustomizationSettings
} from '~/pages/Customizations/utils.ts';
import { useLocation } from '@solidjs/router';

type CustomizationContextVal = {
  brandConfig: PortalCustomizationSettings | null;
  appConfig: ApplicationCustomizationSettings | null;
};

const PortalCustomizationContext = createContext<CustomizationContextVal>();

type PortalCustomizationProps = {
  children: JSXElement;
};

export default function PortalCustomizations(
  props: PortalCustomizationProps
): JSXElement {
  const merged = mergeProps({}, props);
  const previewModeContext: PreviewModeContextValueType | null =
    usePreviewModeContext();
  const location = useLocation();

  const [contextData, setContextData] = createStore<CustomizationContextVal>({
    brandConfig: null,
    appConfig: null
  });

  const getPortalCustomizationQueryConfig = () => {
    switch (globalStore.appContext) {
      case AppContexts.CUSTOMER_PORTAL:
        return {
          queryKey: getPortalCustomizationConfigQuery(),
          queryFn: async () => {
            return previewModeContext?.isPreviewModeEnabled
              ? {}
              : await getCurrentPortalCustomizationConfig();
          },
          retry: retryServerErrors
        };
      case AppContexts.HOSTED_AUTH_PAGES:
      case AppContexts.ENV_APP_PAGES:
        return {
          queryKey: getHostedAuthPageCustomizationQueryKey(),
          queryFn: async () => {
            return previewModeContext?.isPreviewModeEnabled
              ? {}
              : await getHostedAuthCustomization();
          },
          retry: retryServerErrors
        };
      default:
        throw new Error(AppErrors.FAIELD_TO_LOAD_CUSTOMIZATION_CONTEXT);
    }
  };

  const portalCustomizationQuery = createQuery(() =>
    getPortalCustomizationQueryConfig()
  );

  createEffect(() => {
    if (
      !portalCustomizationQuery.isPending &&
      (portalCustomizationQuery.isSuccess ||
        portalCustomizationQuery.isError) &&
      location.pathname
    ) {
      if (portalCustomizationQuery.isError) {
        logger.error(
          `Portal customization not loaded:: ${portalCustomizationQuery.error.message}`
        );
        untrack(() => {
          setContextData('brandConfig', {});
          setContextData('appConfig', {});
        });
        if (globalStore.appContext === AppContexts.CUSTOMER_PORTAL) {
          if (
            portalCustomizationQuery.error?.httpStatus ===
            HttpStatusCode.Unauthorized
          ) {
            throw new Error(AppErrors.CUSTOMER_PORTAL_SESSION_EXPIRED);
          } else if (
            portalCustomizationQuery.error?.httpStatus ===
            HttpStatusCode.NotFound
          ) {
            return;
          }
          throw new Error(AppErrors.CUSTOMER_PORTAL_REQUEST_FAILED);
        }
      }

      logger.info(
        `Portal customization settings found`,
        portalCustomizationQuery.data?.customizationSettings ??
          portalCustomizationQuery.data?.customizations
      );

      const updatedCustomziationSettings = getCustomizationSettingsWithDefaults(
        portalCustomizationQuery.data?.customizationSettings ??
          portalCustomizationQuery.data?.customizations ??
          {}
      );
      const updatedBrandCustomizationSettings =
        updatedCustomziationSettings.portalCustomization ??
        getDefaultPortalCustomizationSettings();
      const updatedAppCustomizationSettings =
        updatedCustomziationSettings.applicationCustomization ??
        getDefaultApplicationCustomizationSettings();

      // apply customization before updating context
      applyBrandCustomization(updatedBrandCustomizationSettings);

      untrack(() => {
        setContextData('brandConfig', updatedBrandCustomizationSettings);
        setContextData('appConfig', updatedAppCustomizationSettings);
        if (contextData.brandConfig?.iconUrl) {
          setGlobalStore('appFavIcon', contextData.brandConfig?.iconUrl!);
        }
        if (
          contextData.appConfig?.name &&
          (globalStore.appContext === AppContexts.HOSTED_AUTH_PAGES ||
            globalStore.appContext === AppContexts.ENV_APP_PAGES)
        ) {
          setGlobalStore('appTitle', contextData.appConfig?.name);
        }
      });
    }
  });

  if (previewModeContext.isPreviewModeEnabled) {
    registerPreviewEvent(PREVIEW_IFRAME_MESSAGES.CUSTOMIZATIONS, (payload) => {
      logger.info(`Customization received from post message`, payload);

      const { applicationCustomization, portalCustomization } = (payload?.data
        ?.customziationSettings ?? {}) as CustomizationSettings;

      portalCustomization && setContextData('brandConfig', portalCustomization);
      applicationCustomization &&
        setContextData('appConfig', applicationCustomization);

      const portalCustomizationToApply = {
        ...getDefaultPortalCustomizationSettings(),
        ...portalCustomization
      };

      applyBrandCustomization(portalCustomizationToApply);

      logger.info('Applied brand settings', portalCustomizationToApply);
    });
    // ask parent to send customization only after registering above
    askCustomizationFromParent();
  }

  return (
    <PortalCustomizationContext.Provider value={contextData}>
      <Show
        when={!portalCustomizationQuery.isPending}
        fallback={<SkLoader />}
      >
        {merged.children}
      </Show>
    </PortalCustomizationContext.Provider>
  );
}

export const usePortalCustomizationContext = (): CustomizationContextVal =>
  useContext(PortalCustomizationContext) as CustomizationContextVal;
